import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Mail, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';

/**
 * VerificationPending Component
 * 
 * Shown after user registers, telling them to check their email for verification link.
 * 
 * ROLE-BASED NAVIGATION (Jan 14, 2026):
 * The "Back to Login" button routes to the appropriate login page based on user type:
 * - Customers → /customer/login/{calendar_symbolic_name} (calendar-scoped login per Rule #3)
 * - Calendar Admins → /login (admin login)
 * 
 * This data is passed via React Router's location.state from the registration page:
 * - email: User's email address
 * - calendarSymbolicName: Calendar URL slug (only for customers)
 * - isCustomer: Boolean flag indicating if user is a customer
 */
const VerificationPending = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract registration context from location state
  // This data is passed from CustomerRegister.jsx or Register.jsx
  const email = location.state?.email || '';
  const calendarSymbolicName = location.state?.calendarSymbolicName || '';  // Calendar URL slug for customers
  const isCustomer = location.state?.isCustomer || false;                    // True if customer registration
  
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleResend = async () => {
    if (!email) {
      setError('Email address not found. Please try registering again.');
      return;
    }

    setResending(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(email)}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        setResendSuccess(true);
      } else {
        setError(data.detail || 'Failed to resend verification email');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  };

  /**
   * Navigate back to the appropriate login page based on user type
   * 
   * CUSTOMER: Goes to /customer/login/{symbolic_name}
   * - Customers use calendar-scoped login (Rule #3 in CONVENTIONS.md)
   * - The calendar context is required for customer authentication
   * 
   * ADMIN: Goes to /login
   * - Calendar Admins use the standard admin login page
   */
  const handleBackToLogin = () => {
    if (isCustomer && calendarSymbolicName) {
      // Customer: Navigate back to calendar-scoped customer login
      navigate(`/customer/login/${calendarSymbolicName}`);
    } else {
      // Admin: Navigate back to standard admin login
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <section className="flex-1 flex items-center justify-center bg-gradient-to-br from-white to-amber-50 py-20">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-yellow-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Check Your Email</h1>
            
            <p className="text-gray-600 mb-2">
              We've sent a verification link to:
            </p>
            <p className="font-semibold text-gray-800 mb-6">
              {email || 'your email address'}
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                Please click the link in the email to verify your account. The link will expire in 24 hours.
              </p>
            </div>

            {resendSuccess ? (
              <div className="flex items-center justify-center gap-2 text-green-600 mb-6">
                <CheckCircle className="w-5 h-5" />
                <span>Verification email sent!</span>
              </div>
            ) : (
              <p className="text-gray-500 text-sm mb-4">
                Didn't receive the email? Check your spam folder or
              </p>
            )}

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-3">
              {!resendSuccess && (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="resend-btn"
                >
                  {resending ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </button>
              )}
              
              {/* Dynamic button text based on user type */}
              <button
                onClick={handleBackToLogin}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {isCustomer ? 'Back to Customer Login' : 'Back to Login'}
              </button>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default VerificationPending;
